import { Theme, alpha } from '@mui/material/styles';
import { Components } from '@mui/material/styles/components';
import Colors from './colors';
import { GRADIENT, TRANSITON_BOUNCE } from './consts';

const components = (theme: Theme): Components => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: Colors.white,
        overflow: 'hidden',
        lineHeight: 1.43,
        color: Colors.lightGray4,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: theme.spacing(0.5),
        fontSize: '1rem',
        '&:hover': {
          backgroundColor: 'transparent', // “none” → “transparent”
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        minHeight: 36,
        borderRadius: 72,
        padding: theme.spacing(1.25, 2.5),
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&.Mui-disabled': {
          backgroundColor: Colors.buttonDisabled,
          color: Colors.white,
        },
      },
      sizeSmall: {
        minHeight: 30,
        fontSize: 12,
        padding: theme.spacing(0.75, 2),
      },
      startIcon: {
        marginLeft: 0,
        marginRight: theme.spacing(1),
      },
      endIcon: {
        marginLeft: theme.spacing(1),
        marginRight: 0,
      },
      iconSizeMedium: {
        '& *:first-child': {
          fontSize: 16,
        },
      },
      contained: {
        boxShadow: 'none',
        '&.Mui-disabled': {
          backgroundColor: Colors.buttonDisabled,
          color: Colors.white,
        },
      },
      containedPrimary: {
        backgroundColor: Colors.primary,
        color: Colors.white,
        '&:hover': {
          backgroundColor: Colors.primaryDark,
        },
        '&.Mui-disabled': {
          backgroundColor: Colors.buttonDisabled,
          color: Colors.white,
        },
      },
      containedSecondary: {
        backgroundColor: Colors.primaryBg,
        color: Colors.primary,
        '&:hover': {
          backgroundColor: Colors.secondaryLight,
        },
        '&.Mui-disabled': {
          backgroundColor: Colors.buttonDisabled,
          color: Colors.white,
        },
      },
      outlined: {
        backgroundColor: Colors.white,
        padding: theme.spacing(1.25, 2.5),
        '&.Mui-disabled': {
          border: 'none',
        },
      },
      outlinedPrimary: {
        color: Colors.primary,
        borderColor: Colors.primary,
        '&:hover': {
          backgroundColor: Colors.primaryHighlight,
        },
        '&.Mui-disabled': {
          backgroundColor: Colors.buttonDisabled,
          color: Colors.white,
        },
      },
      outlinedSecondary: {
        color: Colors.primaryLight,
        backgroundColor: Colors.white,
        borderColor: Colors.primaryLight,
        borderStyle: 'dashed',
        borderRadius: theme.shape.borderRadius / 2,
        textTransform: 'uppercase',
        '&:hover': {
          backgroundColor: Colors.primaryHighlight,
          borderColor: Colors.primaryLight,
          borderStyle: 'dashed',
        },
        '&.Mui-disabled': {
          color: Colors.textDisabled,
          borderColor: Colors.buttonDisabled,
          borderStyle: 'dashed',
        },
      },
      text: {
        backgroundColor: 'transparent',
        padding: theme.spacing(1.25, 2.5),
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      textPrimary: {
        color: Colors.primary,
        '&:hover': {
          color: Colors.primaryDark,
          backgroundColor: 'transparent',
        },
        '&.Mui-disabled': {
          color: Colors.textDisabled,
        },
      },
      textSecondary: {
        backgroundColor: Colors.white,
        color: Colors.primary,
        '&:hover': {
          backgroundColor: Colors.primaryHighlight,
        },
        '&.Mui-disabled': {
          color: Colors.textDisabled,
        },
      },
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      },
      contained: {
        boxShadow: 'none',
      },
      grouped: {
        minWidth: 36,
        '&:not(:first-of-type)': {
          borderLeft: 'none',
          marginLeft: 0,
        },
      },
      groupedContainedPrimary: {
        '&:not(:last-child)': {
          borderRightColor: Colors.whiteDimmed,
        },
      },
      groupedContainedSecondary: {
        '&:not(:last-child)': {
          borderRightColor: Colors.stroke,
        },
      },
      groupedTextPrimary: {
        '&:not(:last-child)': {
          borderRightColor: 'transparent',
        },
      },
      groupedTextSecondary: {
        '&:not(:last-child)': {
          borderRightColor: Colors.stroke,
        },
      },
      groupedOutlined: {
        '&:not(:first-of-type)': {
          marginLeft: 0,
        },
      },
      groupedOutlinedPrimary: {
        '&:not(:last-child)': {
          borderRightColor: Colors.primary,
        },
      },
      groupedOutlinedSecondary: {
        '&:hover': {
          borderColor: Colors.primaryLight,
        },
        '&:not(:last-child)': {
          borderRightColor: Colors.primaryLight,
          '&.Mui-disabled': {
            borderRightColor: Colors.buttonDisabled,
          },
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        outlineWidth: 1,
        outlineStyle: 'solid',
        outlineColor: Colors.stroke,
        borderRadius: theme.shape.borderRadius / 2,
        margin: theme.spacing(0.5, 0),
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      middle: {
        marginTop: theme.spacing(0.75),
        marginBottom: theme.spacing(0.75),
        marginLeft: theme.spacing(1.25),
        marginRight: theme.spacing(1.25),
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        minHeight: 48,
        '&:last-child': {
          borderBottom: 'none',
        },
        [theme.breakpoints.up('sm')]: {
          minHeight: 48,
        },
      },
      gutters: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
      },
      dense: {
        fontSize: '0.875rem',
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        minHeight: 0,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 20,
        fontSize: 16,
        justifyContent: 'center',
        '&:first-of-type': {
          marginRight: theme.spacing(1.75),
        },
        '&:last-of-type': {
          marginLeft: theme.spacing(1.75),
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1.75, 2.5),
        minHeight: 58,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(3.25, 2.5),
        '&:first-child': {
          paddingTop: theme.spacing(2.5),
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2.5),
        backgroundColor: Colors.gray,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      marginNormal: {
        marginTop: 0,
        marginBottom: theme.spacing(3),
      },
      marginDense: {
        marginTop: 0,
        marginBottom: theme.spacing(1.5),
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        ...theme.typography.button,
        color: Colors.textBlack,
        '&.Mui-disabled': {
          color: Colors.textBlack,
        },
        '&.Mui-error': {
          color: Colors.textBlack,
        },
        '&.Mui-focused': {
          color: Colors.textBlack,
        },
      },
      formControl: {
        transform: 'none',
        position: 'relative',
      },
      asterisk: {
        color: Colors.error,
      },
      shrink: {
        transform: 'none',
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        ...theme.typography.subtitle2,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        position: 'relative',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: Colors.stroke,
        borderRadius: theme.shape.borderRadius / 2,
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: Colors.gray,
        },
        '&.Mui-focused': {
          borderColor: Colors.primaryLight,
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: 'white',
          },
        },
        '&.Mui-disabled': {
          backgroundColor: Colors.fieldDisabled,
          '&:hover': {
            backgroundColor: Colors.fieldDisabled,
          },
        },
        '&.Mui-error': {
          borderColor: Colors.error,
        },
      },
      input: {
        borderRadius: theme.shape.borderRadius / 2,
        backgroundColor: 'transparent',
        height: 20,
        padding: theme.spacing(1.25, 2),
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: Colors.textBlack,
        '&::placeholder': {
          color: Colors.textDisabled,
          opacity: 1,
        },
        '&:focus::placeholder': {
          color: Colors.textDisabled,
        },
        '&:-webkit-autofill': {
          transition: theme.transitions.create('background-color', { duration: '3600s', delay: '3600s' }),
        },
        // Additional webkit autofill transitions can be repeated similarly
        '&.Mui-disabled': {
          color: Colors.textGray,
        },
      },
      multiline: {
        height: 'auto',
        minHeight: 20,
        padding: theme.spacing(1.25, 2),
      },
      inputAdornedEnd: {
        paddingRight: 0,
      },
      inputAdornedStart: {
        paddingLeft: 0,
      },
      formControl: {
        '&::placeholder': {
          color: Colors.textDisabled,
          opacity: 1,
        },
        '&:focus::placeholder': {
          color: Colors.textDisabled,
        },
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        color: Colors.textGray,
        fontSize: 14,
        gap: theme.spacing(1),
        padding: theme.spacing(0, 1.5),
        '& > svg': {
          fontSize: 16,
        },
      },
      positionStart: {
        marginRight: 0,
        marginLeft: 0,
      },
      positionEnd: {
        marginRight: 0,
        marginLeft: 0,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      barColorPrimary: {
        backgroundColor: 'transparent',
        background: GRADIENT,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 12,
        background: Colors.textBlack,
        borderRadius: theme.shape.borderRadius / 1.5,
        padding: theme.spacing(0.75, 1.25),
      },
      tooltipPlacementBottom: {
        margin: theme.spacing(1, 0),
      },
      tooltipPlacementTop: {
        margin: theme.spacing(1, 0),
      },
      tooltipPlacementRight: {
        margin: theme.spacing(0, 1),
      },
      tooltipPlacementLeft: {
        margin: theme.spacing(0, 1),
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        ...theme.typography.subtitle1,
        backgroundColor: Colors.primaryBg,
        padding: theme.spacing(0.75),
        height: 30,
        color: Colors.textBlack,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: theme.shape.borderRadius / 2,
        '&:focus': {
          backgroundColor: Colors.primaryBg,
        },
        '& .chip-delete': {
          position: 'absolute',
          padding: theme.spacing(0, 0.75, 0, 2),
          top: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          cursor: 'pointer',
          transition: theme.transitions.create('opacity'),
          background: `linear-gradient(to right, ${alpha(Colors.primaryBg, 0)}, ${Colors.primaryBg} 30%)`,
        },
        '& .chip-delete-icon': {
          transform: 'translate(150%, 0)',
          transition: theme.transitions.create('transform', { easing: TRANSITON_BOUNCE }),
        },
        '&:hover .chip-delete': {
          opacity: 1,
        },
        '&:hover .chip-delete-icon': {
          transform: 'translate(0, 0)',
        },
        '&.Mui-disabled': {
          opacity: 1,
          color: Colors.textDisabled,
        },
      },
      sizeSmall: {
        ...theme.typography.subtitle2,
        height: 28,
        '&:hover': {
          paddingRight: theme.spacing(0.75),
        },
        '& .chip-delete': {
          left: 0,
          padding: 0,
          background: Colors.errorHighlight,
        },
        '& .chip-delete-icon': {
          transform: 'translate(0, 150%)',
        },
      },
      deletable: {
        '&:focus': {
          backgroundColor: Colors.primaryBg,
        },
      },
      label: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      labelSmall: {
        paddingLeft: 0,
        paddingRight: 0,
      },
      deleteIcon: {
        margin: 0,
        width: 'auto',
        height: '1em',
        fontSize: 14,
        color: Colors.error,
        '&:hover': {
          color: Colors.error,
        },
      },
      deleteIconSmall: {
        width: 'auto',
        height: '1em',
        marginLeft: 0,
        marginRight: 0,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 30,
        height: 20,
        padding: 0,
        borderRadius: 20,
      },
      switchBase: {
        padding: 3,
        '&.Mui-checked': {
          transform: 'translateX(9px)',
          color: Colors.white,
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: Colors.primaryLight,
          },
        },
      },
      thumb: {
        width: 14,
        height: 14,
        boxShadow: 'none',
        color: Colors.white,
      },
      track: {
        opacity: 1,
        backgroundColor: Colors.textGray,
      },
      colorSecondary: {
        '& + .MuiSwitch-track': {
          backgroundColor: Colors.white,
        },
        '& .MuiSwitch-thumb': {
          color: Colors.textDisabled,
        },
        '&.Mui-checked': {
          color: Colors.primaryLight,
          '& .MuiSwitch-thumb': {
            color: Colors.primaryLight,
          },
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: Colors.white,
          },
        },
      },
    },
  },
});

export default components;
